import { useState } from 'preact/hooks';
import { RESOURCES, houseRules, longestRoadLength, type BotKind, type GameView, type Resources } from '../engine';
import { isMuted, setMuted } from './audio';
import { useKeepAwakeSetting, useWakeLock, wakeLockSupported } from './wake';
import { useAutoPlay, useTurnClock } from './effects';
import type { GameClient, LogEntry } from './game-client';
import { PushToggle } from './push';
import { useSeatColor } from './colors';
import { memo } from './memo';
import { BOT_LABELS, RESOURCE_COLORS, RESOURCE_LABELS, RESOURCE_TEXT } from './theme';
import { HOUSE_RULE_INFO, PlayerName } from './widgets';

export function WinnerPanel({ client, winner, onExit }: { client: GameClient; winner: number; onExit?: () => void }) {
  const { view, names } = client;
  const min = Math.max(view.config.victoryPointsToWin, view.players[view.currentPlayer]!.victoryPoints) + 1;
  const [target, setTarget] = useState(Math.max(min, view.config.victoryPointsToWin + 2));
  const choices = Array.from({ length: 11 }, (_, i) => min + i);
  const canDecide = !!(client.continueTo || client.rematch || client.backToLobby || onExit);
  return (
    <div class="panel winner" data-testid="winner">
      <h2>
        <PlayerName seat={winner} names={names} /> wins with {view.players[winner]!.victoryPoints} points!
      </h2>
      {client.continueTo && (
        <div class="row wrap">
          <span>Keep playing to</span>
          <select
            data-testid="continue-target"
            value={String(target)}
            onChange={(e) => setTarget(Number((e.target as HTMLSelectElement).value))}
          >
            {choices.map((n) => (
              <option key={n} value={String(n)}>
                {n} points
              </option>
            ))}
          </select>
          <button class="primary" data-testid="continue-game" onClick={() => client.continueTo?.(target)}>
            Keep playing
          </button>
        </div>
      )}
      <div class="row wrap">
        {client.rematch && (
          <button data-testid="play-again" onClick={client.rematch}>
            Play again with the same settings
          </button>
        )}
        {client.backToLobby && (
          <button class="secondary" data-testid="back-to-lobby" onClick={client.backToLobby}>
            Back to the lobby
          </button>
        )}
        {onExit && <button onClick={onExit}>New game</button>}
      </div>
      {!canDecide && <p class="muted" data-testid="waiting-host">Waiting for the host to decide what's next…</p>}
    </div>
  );
}

function ActiveRules({ view }: { view: GameView }) {
  const rules = houseRules(view);
  const on = HOUSE_RULE_INFO.filter((r) => rules[r.key]);
  return (
    <p class="muted small" data-testid="active-rules">
      House rules: {on.length ? on.map((r) => r.label).join(' · ') : 'none'}
    </p>
  );
}

export function PlayersPanel({
  view,
  names,
  connected,
  cpu,
  bots,
  focus = null,
  onHover,
  onPin,
}: {
  view: GameView;
  names: readonly string[];
  connected?: boolean[];
  cpu?: boolean[];
  bots?: (BotKind | undefined)[];
  /** The player whose pieces are spotlighted on the board. */
  focus?: number | null;
  onHover?: (seat: number | null) => void;
  /** Tap a row to keep its pieces spotlighted (tap again to stop). */
  onPin?: (seat: number) => void;
}) {
  const anyCpu = !!cpu?.some(Boolean);
  const target = view.config.victoryPointsToWin;
  return (
    <div class="panel players-panel" data-testid="players-panel">
      <h2>Players</h2>
      <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th />
            <th title="Victory points">VP</th>
            <th title="Resource cards">Cards</th>
            <th title="Development cards">Dev</th>
            <th title="Knights played">
              <span class="long">Knights</span>
              <span class="short">Kn</span>
            </th>
            <th title="Longest road">
              <span class="long">Road</span>
              <span class="short">Rd</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {view.players.map((p, i) => (
            <tr
              key={i}
              class={[i === view.currentPlayer && 'current', i === focus && 'focused', p.victoryPoints >= target - 1 && view.phase.kind !== 'gameOver' && 'near-win']
                .filter(Boolean)
                .join(' ')}
              tabindex={0}
              title="Tap to show their pieces on the board"
              data-testid={`player-row-${i}`}
              onPointerEnter={(e) => e.pointerType === 'mouse' && onHover?.(i)}
              onPointerLeave={(e) => e.pointerType === 'mouse' && onHover?.(null)}
              onClick={() => onPin?.(i)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onPin?.(i))}
            >
              <td>
                <PlayerName
                  seat={i}
                  names={names}
                  tag={
                    anyCpu && (
                      <span class="tag-slot">
                        {cpu?.[i] && (
                          <span class="cpu-tag" title={bots?.[i] ? `${BOT_LABELS[bots[i]!].label} CPU: ${BOT_LABELS[bots[i]!].detail}` : 'Played by the computer'}>
                            CPU
                          </span>
                        )}
                      </span>
                    )
                  }
                />
                {/* Always rendered, even empty, so every row keeps the same height as badges come and go. */}
                <div class="row-meta">
                  {connected && !connected[i] && (
                    <span class="offline" title="Disconnected">
                      away
                    </span>
                  )}
                  {p.victoryPoints >= target - 1 && view.phase.kind !== 'gameOver' && (
                    <span class="near-win-tag" data-testid={`near-win-${i}`} title="Close to winning">
                      {p.victoryPoints >= target ? 'wins on their turn' : '1 to win'}
                    </span>
                  )}
                  {view.buildRequests?.[i] && (
                    <span class="build-tag" title="Will build when this turn ends">
                      builds next
                    </span>
                  )}
                  <RollGains gains={view.rollGains?.[i]} seat={i} />
                </div>
              </td>
              <td>{p.victoryPoints}</td>
              <td>{p.resourceCount}</td>
              <td>
                {p.devCardCount}
                {p.victoryPointCards > 0 && (
                  <span class="badge" title="Victory Point cards (revealed when bought)">
                    +{p.victoryPointCards} VP
                  </span>
                )}
              </td>
              <td>
                {p.playedDevCards.filter((c) => c === 'knight').length}
                {view.largestArmy === i && <span class="badge" title="Largest Army">★</span>}
              </td>
              <td>
                {longestRoadLength(view, i)}
                {view.longestRoad === i && <span class="badge" title="Longest Road">★</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <p class="muted small">
        Bank: {RESOURCES.map((r) => `${RESOURCE_LABELS[r]} ${view.bank[r]}`).join(' · ')} · Dev cards {view.devDeckCount}
      </p>
      <ActiveRules view={view} />
    </div>
  );
}
/** What a player collected from this turn's roll, shown until the next turn so nobody has to catch it live. */
function RollGains({ gains, seat }: { gains: Resources | undefined; seat: number }) {
  if (!gains || RESOURCES.every((r) => gains[r] === 0)) return null;
  return (
    <span class="roll-gains" data-testid={`roll-gains-${seat}`} title="Collected from this turn's roll">
      {RESOURCES.filter((r) => gains[r] > 0).map((r) => (
        <span key={r} class="mini-chip" style={{ background: RESOURCE_COLORS[r], color: RESOURCE_TEXT[r] }} title={RESOURCE_LABELS[r]}>
          +{gains[r]}
        </span>
      ))}
    </span>
  );
}

/** Its own component so the once-a-second tick re-renders only the clock. */
export function TurnClock({ turn, seconds }: { turn: string; seconds: number | null | undefined }) {
  const remaining = useTurnClock(turn, seconds);
  if (remaining === null) return null;
  return (
    <span class={`clock ${remaining === 0 ? 'late' : remaining <= 15 ? 'soon' : ''}`} data-testid="clock">
      {remaining === 0 ? "Time's up" : `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`}
    </span>
  );
}

/** Memoised: the log holds the whole game, and nothing else on the screen should make it re-render. */
export const LogPanel = memo(function LogPanel({ log }: { log: readonly LogEntry[] }) {
  const seatColor = useSeatColor();
  return (
    <div class="panel log" data-testid="log">
      <h2>Log</h2>
      <ol>
        {log
          .slice()
          .reverse()
          .map((l) =>
            l.divider ? (
              <li key={l.id} class="log-turn">
                {l.seat !== undefined && <span class="swatch" style={{ background: seatColor(l.seat) }} />}
                {l.text}
              </li>
            ) : (
              <li key={l.id}>
                {l.seat !== undefined && <span class="dot" style={{ background: seatColor(l.seat) }} />}
                {l.text}
              </li>
            ),
          )}
      </ol>
    </div>
  );
});


export function OptionsPanel({
  client,
  auto,
  setAuto,
  onQuit,
}: {
  client: GameClient;
  auto: ReturnType<typeof useAutoPlay>[0];
  setAuto: ReturnType<typeof useAutoPlay>[1];
  onQuit?: () => void;
}) {
  const [muted, setMutedState] = useState(isMuted);
  // Mounted for the whole game screen, so the screen stays on while a game is showing.
  const [keepAwake, setKeepAwake] = useKeepAwakeSetting();
  useWakeLock(keepAwake && client.view.phase.kind !== 'gameOver');
  return (
    <div class="panel options" data-testid="options">
      <h2>Options</h2>
      {client.seat !== null && (
        <div class="option-list" data-testid="auto-play">
          <label class="check">
            <input type="checkbox" data-testid="auto-roll" checked={auto.roll} onChange={(e) => setAuto({ ...auto, roll: (e.target as HTMLInputElement).checked })} />
            <span>Auto-roll at the start of my turn</span>
          </label>
          <label class="check">
            <input type="checkbox" data-testid="auto-end" checked={auto.end} onChange={(e) => setAuto({ ...auto, end: (e.target as HTMLInputElement).checked })} />
            <span>Auto-end my turn when nothing's left to do</span>
          </label>
          {client.push && <PushToggle push={client.push} />}
        </div>
      )}
      <div class="row wrap tools">
        <button
          class="small secondary"
          data-testid="mute"
          aria-pressed={!muted}
          onClick={() => {
            setMuted(!muted);
            setMutedState(!muted);
          }}
        >
          Sound: {muted ? 'off' : 'on'}
        </button>
        {wakeLockSupported() && (
          <button class="small secondary" data-testid="keep-awake" aria-pressed={keepAwake} onClick={() => setKeepAwake(!keepAwake)}>
            Keep screen on: {keepAwake ? 'on' : 'off'}
          </button>
        )}
        {client.undo && (
          <button class="small secondary" data-testid="undo" onClick={() => client.undo?.()}>
            Undo last move
          </button>
        )}
        {onQuit && (
          <button class="small danger" onClick={() => confirm('Abandon this game?') && onQuit()}>
            Quit game
          </button>
        )}
      </div>
    </div>
  );
}
